/**
 * ============================================================================
 * TaskFlow ERP
 * Module : Campaign Management
 * Feature: Prospect Import
 *
 * File:
 * src/modules/campaign/prospectImport/utils/importValidation.js
 *
 * Description:
 * Common validation utilities for Prospect Import.
 *
 * This file contains reusable validation functions only.
 * No UI code.
 * No API calls.
 * No business logic.
 * ============================================================================
 */

import {
    ACCEPTED_FILE_TYPES,
    ACCEPTED_MIME_TYPES,
    MAX_FILE_SIZE_BYTES,
    STATUS_VARIANTS
} from "../constants/campaignImportConstants";

/**
 * ============================================================================
 * Validate Selected File
 * ============================================================================
 */
export const validateFile = (file) => {

    if (!file) {
        return {
            valid: false,
            message: "Please select a file."
        };
    }

    const typeValidation = validateFileType(file);

    if (!typeValidation.valid) {
        return typeValidation;
    }

    const sizeValidation = validateFileSize(file);

    if (!sizeValidation.valid) {
        return sizeValidation;
    }

    return {
        valid: true,
        message: ""
    };
};

/**
 * ============================================================================
 * Validate File Extension
 * ============================================================================
 */
export const validateFileType = (file) => {

    if (!file) {
        return {
            valid: false,
            message: "No file selected."
        };
    }

    const fileName = file.name.toLowerCase();

    const validExtension = ACCEPTED_FILE_TYPES.some(
        extension => fileName.endsWith(extension)
    );

    const validMimeType = ACCEPTED_MIME_TYPES.includes(file.type);

    if (!validExtension && !validMimeType) {
        return {
            valid: false,
            message: "Only Excel (.xlsx, .xls) and CSV files are allowed."
        };
    }

    return {
        valid: true,
        message: ""
    };
};

/**
 * ============================================================================
 * Validate File Size
 * ============================================================================
 */
export const validateFileSize = (file) => {

    if (!file) {
        return {
            valid: false,
            message: "No file selected."
        };
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {

        return {
            valid: false,
            message: `Maximum file size allowed is ${Math.round(MAX_FILE_SIZE_BYTES / 1024 / 1024)} MB.`
        };

    }

    return {
        valid: true,
        message: ""
    };
};

/**
 * ============================================================================
 * Email Validation
 * ============================================================================
 */
export const validateEmail = (email = "") => {

    if (!email) {
        return false;
    }

    const regex =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return regex.test(email);
};

/**
 * ============================================================================
 * Mobile Number Validation
 * ============================================================================
 */
export const validateMobile = (mobile = "") => {

    if (!mobile) {
        return false;
    }

    const regex = /^[0-9]{10,15}$/;

    return regex.test(mobile);
};

/**
 * ============================================================================
 * Required Field Validation
 * ============================================================================
 */
export const validateRequired = (value) => {

    if (value === null || value === undefined) {
        return false;
    }

    if (typeof value === "string") {
        return value.trim().length > 0;
    }

    return true;
};

/**
 * ============================================================================
 * Format File Size
 * ============================================================================
 */
export const formatFileSize = (bytes = 0) => {

    if (bytes < 1024) {
        return `${bytes} Bytes`;
    }

    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(2)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * ============================================================================
 * Get Status Badge Variant
 * ============================================================================
 */
export const getStatusVariant = (status = "") => {

    return STATUS_VARIANTS[status] || "secondary";
};

/**
 * ============================================================================
 * Check if Record is Editable
 * ============================================================================
 */
export const isEditable = (status = "") => {

    return status !== "Verified";
};

/**
 * ============================================================================
 * Check if Compare is Allowed
 * ============================================================================
 */
export const canCompare = (record) => {

    return !!record;
};

/**
 * ============================================================================
 * Check if Import can proceed
 * ============================================================================
 */
export const canImport = (summary) => {

    if (!summary) {
        return false;
    }

    return summary.valid_rows > 0;
};

/**
 * ============================================================================
 * Count Selected Records
 * ============================================================================
 */
export const getSelectedCount = (records = []) => {

    return records.filter(record => record.selected).length;
};

/**
 * ============================================================================
 * Check Single Selection
 * ============================================================================
 */
export const hasSingleSelection = (records = []) => {

    return getSelectedCount(records) === 1;
};

/**
 * ============================================================================
 * Check Multiple Selection
 * ============================================================================
 */
export const hasMultipleSelection = (records = []) => {

    return getSelectedCount(records) > 1;
};